import { createTheme, Shadows } from '@mui/material/styles';

/* drift-check-skip-begin: データビズ系列色5exports のみ hex凍結・トークン化対象外 */
/* ---------- データビズ系列色定数（CUD配色・値不変） ---------- */
export const poChartSeriesColors = [
  '#FF4B00', '#005AFF', '#03AF7A', '#4DC4FF', '#F6AA00', '#FFF100', '#990099',
];
export const poDashboardChartColors = [
  '#005AFF', '#03AF7A', '#F6AA00', '#990099', '#4DC4FF',
];
export const poRankingColors = [
  '#FF4B00', '#005AFF', '#03AF7A', '#F6AA00', '#4DC4FF', '#990099', '#84919E',
];
export const poSaleCompanyChartColors = {
  border:       '#ccc',
  borderStrong: '#000',
  rowBg:        '#f5f5f5',
  selectedBg:   '#e3f2fd',
  selectedRow:  '#bbdefb',
  rowBgAlt:     '#ffffff',
  divider:      '#eee',
};
export const poChartTooltipColors = {
  bg:     '#ffffff',
  border: 'rgb(204, 204, 204)',
  svgFill: '#666',
};

/* ---------- MUI Palette 型拡張（MAJOR-1: 型のみ・値なし） ---------- */
declare module '@mui/material/styles' {
  interface Palette {
    category: {
      entry: string; entryText: string;
      tossup: string; tossupText: string;
      closing: string; closingText: string;
    };
    rowStatus: {
      noentryStrong: string; noentry: string;
      fushutsuStrong: string; fushutsu: string;
      jisetsuStrong: string; jisetsu: string;
      mikomiStrong: string; mikomi: string;
      calling: string;
    };
    statusChip: {
      draft: string; draftBg: string;
      pending: string; pendingBg: string;
      confirmed: string; confirmedBg: string;
      billed: string; billedBg: string;
      awaitPay: string; awaitPayBg: string;
      paid: string; paidBg: string;
      payPrep: string; payPrepBg: string;
      payout: string; payoutBg: string;
      hold: string; holdBg: string;
      returned: string; returnedBg: string;
      cancelled: string; cancelledBg: string;
    };
  }
  interface PaletteOptions {
    category?: Palette['category'];
    rowStatus?: Palette['rowStatus'];
    statusChip?: Palette['statusChip'];
  }
}
/* drift-check-skip-end */

/* ---------- AppShell サイドバー配色定数（skip外・drift-check[1]対象） ---------- */
export const poSidebarTokens = {
  text:         'var(--color-ai-pale)',     /* --color-ai-pale: base.css L213 */
  textActive:   'var(--color-white, #ffffff)',        /* --color-white */
  hover:        'rgba(232,238,244,0.07)',
  activeBg:     'rgba(255,255,255,0.12)',
  activeBorder: 'var(--color-kohaku, #b8860b)',        /* --color-kohaku */
  groupLabel:   'var(--color-sumi-muted, #9ca3af)', /* --color-sumi-muted: base.css L224-227 nav-group-label */
  divider:      'rgba(255,255,255,0.08)',
  hoverText:    'rgba(232,238,244,0.5)',
  mutedText:    'rgba(232,238,244,0.45)',
};

/* ---------- コンポーネント固有カラー定数（skip外・drift-check[1]対象） ---------- */
export const poBulkTableColors = {
  legacyZebraOdd:  'var(--color-ai-pale)',  /* --color-ai-pale: #e8eef4 */
  zebraEven:       'var(--color-white, #ffffff)',   /* --color-white */
  zebraOdd:        'var(--color-ai-pale)',           /* --color-ai-pale: #e8eef4（R10配色統一・legacyZebraOddと同値） */
  borderGray:      'rgb(224, 224, 224)',             /* no-token: table border */
};
export const poCalendarColors = {
  headerBg:    'rgb(96, 125, 139)',    /* no-token: calendar header */
  sunday:      'rgb(255, 107, 107)',   /* no-token: sunday */
  saturday:    'rgb(100, 181, 246)',   /* no-token: saturday */
  today:       'rgb(179, 229, 252)',   /* no-token: today */
  todayHover:  'rgb(129, 212, 250)',   /* no-token: today hover */
  cellHover:   'rgb(245, 245, 245)',   /* no-token: cell hover */
  todayNum:    'rgb(2, 119, 189)',     /* no-token: today number */
  sundayNum:   'rgb(229, 57, 53)',     /* no-token: sunday number */
  saturdayNum: 'rgb(21, 101, 192)',    /* no-token: saturday number */
  borderOuter: 'rgb(221, 221, 221)',   /* no-token: outer border */
  borderCell:  'rgb(238, 238, 238)',   /* no-token: cell border */
};

/* ---------- Dashboard entryアクセント（no-token系列色・skip外・rgb形式） ---------- */
export const poDashboardEntryShades = {
  shotDark:   'rgb(163, 77, 32)',     /* no-token: entry shot dark hover */
  stockLight: 'rgb(232, 168, 124)',   /* no-token: entry stock light */
};

/* ---------- 汎用系列色（DataLine/DataBarChart・CUD範囲外・rgb形式） ---------- */
export const poChartAltSeriesColors = [
  'rgb(136, 132, 216)', 'rgb(130, 202, 157)', 'rgb(255, 198, 88)',
  'rgb(255, 127, 127)', 'rgb(164, 222, 108)',
]; /* no-token */

/* Japanese Elegance — MUI theme bridge
 * 正本: /home/nhayashi/rockman-po/images/ui/html/base.css (FROZEN 2026-07-24)
 * 規律:
 *   palette 標準スロット = hexリテラル。各行に /* --token-name *\/ コメント（機械床突合キー）
 *   palette.category / rowStatus / statusChip = var(--token)（MUI色計算非経由のため破綻しない）
 *   shadows / styleOverrides / sx = var(--token)（素のCSSプロパティ値として有効）
 *   cssVariables:true で MUI が --mui-palette-* を生成し alpha()/lighten()/darken() 経路が安定 */
const poTheme = createTheme({
  cssVariables: true,
  shape: {
    borderRadius: 6, /* --radius-sm: ボタン/入力系既定 */
  },
  palette: {
    /* ===== 標準スロット: hexリテラル（MUI色計算要件 + 機械床突合キー） ===== */
    primary: {
      main:         '#1e3a5f', /* --color-ai */
      dark:         '#0f1d2f', /* --color-ai-dark */
      light:        '#2d4a6f', /* --color-ai-light */
      contrastText: '#ffffff', /* --color-white */
    },
    secondary: {
      main:         '#b8860b', /* --color-kohaku */
      dark:         '#a07608', /* --color-kohaku-text */
      light:        '#d4a574', /* --color-kohaku-light */
      contrastText: '#ffffff', /* --color-white */
    },
    text: {
      primary:   '#374151', /* --color-sumi */
      secondary: '#6b7280', /* --color-sumi-light */
      disabled:  '#9ca3af', /* --color-sumi-muted */
    },
    background: {
      default: '#fafafa', /* --color-washi */
      paper:   '#ffffff', /* --color-white */
    },
    success: {
      main:         '#276e50', /* --sem-success */
      light:        '#e6f4ee', /* --sem-success-bg */
      dark:         '#276e50', /* --sem-success */
      contrastText: '#ffffff', /* --color-white */
    },
    warning: {
      main:         '#9c4a08', /* --sem-warning */
      light:        '#fdf3e0', /* --sem-warning-bg */
      dark:         '#9c4a08', /* --sem-warning */
      contrastText: '#ffffff', /* --color-white */
    },
    error: {
      main:         '#a53229', /* --sem-error */
      light:        '#f6d5d1', /* --sem-error-bg */
      dark:         '#a53229', /* --sem-error */
      contrastText: '#ffffff', /* --color-white */
    },
    info: {
      main:         '#28557c', /* --sem-info */
      light:        '#e8f1f8', /* --sem-info-bg */
      dark:         '#28557c', /* --sem-info */
      contrastText: '#ffffff', /* --color-white */
    },
    divider: '#e5e5e0', /* --color-border */
    action: {
      hover:              'rgba(30, 58, 95, 0.05)',  /* --state-hover */
      selected:           'rgba(30, 58, 95, 0.10)',  /* --state-selected */
      disabled:           '#9ca3af',                 /* --state-disabled-fg */
      disabledBackground: '#f0f0ec',                 /* --state-disabled-bg */
      focus:              'rgba(45, 74, 111, 0.45)', /* --state-focus-ring */
    },
    /* ===== カスタムキー: var(--token)参照（MUI色計算非経由・AC4趣旨適合） ===== */
    /* spec §2.2: 実績系統色 palette.category.* */
    category: {
      entry:      'var(--cat-entry)',
      entryText:  'var(--cat-entry-text)',
      tossup:     'var(--cat-tossup)',
      tossupText: 'var(--cat-tossup-text)',
      closing:    'var(--cat-closing)',
      closingText:'var(--cat-closing-text)',
    },
    /* spec §2.2: 行背景色系統 palette.rowStatus.* */
    rowStatus: {
      noentryStrong:  'var(--row-cl-noentry-strong)',
      noentry:        'var(--row-cl-noentry)',
      fushutsuStrong: 'var(--row-fushutsu-strong)',
      fushutsu:       'var(--row-fushutsu)',
      jisetsuStrong:  'var(--row-jisetsu-strong)',
      jisetsu:        'var(--row-jisetsu)',
      mikomiStrong:   'var(--row-mikomi-strong)',
      mikomi:         'var(--row-mikomi)',
      calling:        'var(--row-calling)',
    },
    /* spec §2.2: 明細11ステータス配色 palette.statusChip.* */
    statusChip: {
      draft:      'var(--st-01-draft)',     draftBg:     'var(--st-01-draft-bg)',
      pending:    'var(--st-02-pending)',   pendingBg:   'var(--st-02-pending-bg)',
      confirmed:  'var(--st-03-confirmed)',confirmedBg: 'var(--st-03-confirmed-bg)',
      billed:     'var(--st-04-billed)',    billedBg:    'var(--st-04-billed-bg)',
      awaitPay:   'var(--st-05-await-pay)',awaitPayBg:  'var(--st-05-await-pay-bg)',
      paid:       'var(--st-06-paid)',      paidBg:      'var(--st-06-paid-bg)',
      payPrep:    'var(--st-07-pay-prep)', payPrepBg:   'var(--st-07-pay-prep-bg)',
      payout:     'var(--st-08-payout)',   payoutBg:    'var(--st-08-payout-bg)',
      hold:       'var(--st-09-hold)',      holdBg:      'var(--st-09-hold-bg)',
      returned:   'var(--st-10-returned)', returnedBg:  'var(--st-10-returned-bg)',
      cancelled:  'var(--st-11-cancelled)',cancelledBg: 'var(--st-11-cancelled-bg)',
    },
  },
  typography: {
    fontFamily: 'var(--font-sans)',
    fontSize: 14,
  },
  shadows: [
    'none',
    'var(--shadow-soft)',
    'var(--shadow-card)',
    'var(--shadow-elevated)',
    ...Array(21).fill('var(--shadow-elevated)'),
  ] as Shadows,
  components: {
    /* body のみ 14px/1.6 — html は 16px 据え置き（MUI rem 基準を保護） base.css L125 */
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '14px',
          lineHeight: 1.6,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontFamily: 'var(--font-sans)',
          height: '36px',        /* --control-h: base.css L130 */
          fontSize: '13px',      /* base.css L132 */
          borderRadius: '6px',   /* --radius-sm: base.css L131 */
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 1 },
      styleOverrides: {
        root: {
          borderRadius: '10px', /* --radius-md: カード面 base.css L154 */
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 1 },
      styleOverrides: {
        root: {
          borderRadius: '10px', /* --radius-md: base.css L154 */
          boxShadow: 'var(--shadow-soft)', /* base.css L154 .po-card */
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: { fontFamily: 'var(--font-sans)' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-13)',
            color: 'var(--color-sumi)',
            borderRadius: 'var(--radius-sm)',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-border)',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-ai-light)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-ai)',
          },
        },
        /* borderColor は MuiTextField.styleOverrides へ一本化（二重定義回避・フォルテレビューA1） */
      },
    },
    /* 素Alert（variant=standard）を semantic token へ一括token化（round2-a R6・MUIデフォルト#fdeded根絶。sx指定済Alertは sx > styleOverrides でsx優先＝非回帰） */
    MuiAlert: {
      styleOverrides: {
        standardError: {
          backgroundColor: 'var(--sem-error-bg)',
          color: 'var(--sem-error)',
          '& .MuiAlert-icon': { color: 'var(--sem-error)' },
        },
        standardSuccess: {
          backgroundColor: 'var(--sem-success-bg)',
          color: 'var(--sem-success)',
          '& .MuiAlert-icon': { color: 'var(--sem-success)' },
        },
        standardWarning: {
          backgroundColor: 'var(--sem-warning-bg)',
          color: 'var(--sem-warning)',
          '& .MuiAlert-icon': { color: 'var(--sem-warning)' },
        },
        standardInfo: {
          backgroundColor: 'var(--sem-info-bg)',
          color: 'var(--sem-info)',
          '& .MuiAlert-icon': { color: 'var(--sem-info)' },
        },
      },
    },
  },
});

export default poTheme;
